/**
 * Lazy-loaded photo thumbnail.
 *
 * The admin API requires the Cognito Authorization header on every photo fetch,
 * which a native `<img src>` can't send. We use IntersectionObserver to wait
 * until the thumbnail scrolls into view, then `fetchPhotoBlobUrl` it through
 * authedFetch and stash the resulting object URL in state.
 *
 * Object URLs are revoked on unmount so we don't leak blob handles when the
 * user navigates away from the detail screen.
 */

import { useEffect, useRef, useState } from 'react';
import { fetchPhotoBlobUrl, fetchPhotoProxyBlobUrl, PhotoMissingError } from '../api.js';
import { log } from '../env.js';

export function PhotoThumb({
  jobId,
  photoId,
  promptKey,
  caption,
  mimeType,
  version,
  onOpen,
}: {
  jobId: string;
  photoId: string;
  promptKey: string;
  caption?: string;
  mimeType?: string;
  /** Manifest `uploadedAt`. Changes when the stored bytes are replaced, so a rotate refetches. */
  version?: string;
  onOpen: (blobUrl: string) => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The manifest exists but the bytes never reached storage. Permanent for
  // this photo id — rendered as a placeholder, not a retry tile.
  const [missing, setMissing] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const elRef = useRef<HTMLButtonElement | null>(null);
  // Whichever URL is currently in the <img>, so cleanup revokes the right one
  // after an onError swap, and so the swap only ever happens once per photo.
  const liveUrlRef = useRef<string | null>(null);
  const proxyFallbackRef = useRef(false);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    let cancelled = false;
    let loadedUrl: string | null = null;
    proxyFallbackRef.current = false;
    setBlobUrl(null);
    setError(null);
    setMissing(false);

    const load = () => {
      void fetchPhotoBlobUrl(jobId, photoId)
        .then((url) => {
          if (cancelled) {
            if (url.startsWith('blob:')) URL.revokeObjectURL(url);
            return;
          }
          loadedUrl = url;
          liveUrlRef.current = url;
          setBlobUrl(url);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          if (e instanceof PhotoMissingError) {
            setMissing(true);
            return;
          }
          log('photo load failed', photoId, e);
          setError(e instanceof Error ? e.message : 'load failed');
        });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            observer.disconnect();
            load();
            break;
          }
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
      // Only revoke blob: object URLs — https presigned S3 URLs must not be.
      const live = liveUrlRef.current ?? loadedUrl;
      if (live?.startsWith('blob:')) URL.revokeObjectURL(live);
      liveUrlRef.current = null;
    };
  }, [jobId, photoId, retryKey, version]);

  /**
   * The presigned URL resolved but the browser would not render it. Nothing
   * upstream can see this — the failure happens inside the <img> — so retry
   * once through the authenticated byte proxy before showing the error state.
   */
  const handleImgError = () => {
    const current = liveUrlRef.current;
    if (proxyFallbackRef.current || !current || current.startsWith('blob:')) {
      setBlobUrl(null);
      setError('load failed');
      return;
    }
    proxyFallbackRef.current = true;
    log('presigned photo URL did not render, falling back to proxy', photoId);
    void fetchPhotoProxyBlobUrl(jobId, photoId)
      .then((url) => {
        liveUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch((e: unknown) => {
        setBlobUrl(null);
        if (e instanceof PhotoMissingError) {
          setMissing(true);
          return;
        }
        log('photo proxy fallback failed', photoId, e);
        setError(e instanceof Error ? e.message : 'load failed');
      });
  };

  if (missing) {
    return (
      <div className="photo-thumb photo-thumb--missing" title={promptKey}>
        <div className="photo-thumb__missing">Photo never finished uploading from the device</div>
        <div className="photo-thumb__caption">{caption ?? promptKey}</div>
      </div>
    );
  }

  return (
    <button
      type="button"
      ref={elRef}
      className="photo-thumb"
      onClick={() => {
        if (blobUrl) {
          onOpen(blobUrl);
        } else if (error) {
          setError(null);
          setRetryKey((value) => value + 1);
        }
      }}
      title={error ? `Retry ${promptKey}` : promptKey}
    >
      {blobUrl ? (
        mimeType === 'application/pdf' ? (
          <span>Open PDF</span>
        ) : (
          <img
            key={version ?? photoId}
            src={blobUrl}
            alt={promptKey}
            loading="lazy"
            onError={handleImgError}
          />
        )
      ) : (
        <div className="photo-thumb__loading">{error ? 'Retry photo' : 'loading…'}</div>
      )}
      <div className="photo-thumb__caption">{caption ?? promptKey}</div>
    </button>
  );
}
